/* Suggested rules — built to the "Lumiq Redesign" design: Connection → Dataset → Rules.
 *
 * **The steps are the screen.** A source, then a table in it (each marked with how many to review, "Nothing to review" or
 * "Not generated yet" from one read for the estate), then that table's rules grouped by tier with
 * Create rule on each. An earlier build followed an older export that added an eight-step lifecycle
 * panel and a sample rule form below the steps; the current design ends at the tiers, so both are
 * gone — the rule form's own "next" line says what happens after Create rule, where it happens.
 *
 * Four things the reference is deliberate about, all of which this screen holds.
 *
 * **The AI status is the authority, not the list.** A model row appears only when the run's status
 * is `contributed` — `visibleSuggestions` enforces it, and the reference's own comment says why:
 * filtering by tier alone "would let a run reporting a rejected answer still show model rows if one
 * survived serialisation". Our stored rows and our status come from different statements, so that
 * run is representable. Where the AI tier would be, the status chip and its sentence stand in.
 *
 * **The evidence tier is a 3px rule, never a tinted card.** Declared, inferred and model-proposed
 * take one colour each on the left edge of their card header, plus a chip whose dot is *filled* for
 * the two deterministic tiers and a hollow ring for the model one — so the distinction survives
 * greyscale rather than resting on hue.
 *
 * **An AI row is annotated inside a table that otherwise looks identical.** Three additions the
 * reference makes and this screen makes: the parameters cell says a person supplies the values, the
 * Why cell says Lumiq wrote the sentence, and the action carries a "requires review" stamp. That is
 * how a model proposal stays visibly weaker evidence without being visually louder.
 *
 * **Every state has its own words.** Four empty states (no dataset, never generated, thin metadata,
 * nothing justified), a failure band that keeps the last good result visible, and a skeleton for
 * both the tiles and the rows. None reads as a broken feature, which is the reference's stated aim
 * and why it writes four sentences where one would have fitted.
 *
 * **Since migration 0124 the rows are acted on here.** Apply authors a *draft* rule through
 * `core.rules` and opens its two-eyes activation request; Bind attaches it to the dataset once two
 * other people have activated it; Dismiss records a reason. Each row reads its rule's progress live,
 * and the list may have been generated automatically after a profile — the run note says which.
 *
 * What this screen does **not** carry, because the reference does not: a composed claim sentence
 * per row, a `· dataset` suffix on every row, and the closing "there is no accept route here"
 * footer. All three came from the older export or from the previous build.
 */

import { useEffect, useState } from 'react';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';

import { useDatasetNames } from '@/features/catalog-browser';
import { DatasetObjectTypeIcon } from '@/shared/ui/DatasetObjectTypeIcon';
import { StepTrail } from '@/shared/ui/StepTrail';
import { SCREEN_PATHS } from '@/shared/routes';
import type { RulePrefill } from '@/shared/routes';

import {
  useRegenerateSuggestions,
  useRulegenCapabilities,
  useSuggestions,
} from '../api/rulegen';
import type { CandidateSource, GenerationOutcome, Suggestion } from '../api/rulegen';
import { useGroupName, useSuggestionCatalogue } from '../api/catalogue';
import { SuggestionSourcePicker, SuggestionTablePicker } from './SuggestionsPicker';
import { RecommendationRow } from './RecommendationRow';
import { CoverageReadout } from './CoverageReadout';
import { FilterBar, Observations, RunNote } from './ReviewPanels';
import { coverageByDimension, itemsFromSuggestions } from '../model/coverage';
import {
  NO_FILTER,
  aiPresentation,
  editableParameters,
  groupedByTier,
  isFiltered,
  matchesFilter,
  originCounts,
  outcomeFromRun,
  presentedStatus,
  showsAiStandIn,
  statusCounts,
  tierDotIsFilled,
  tierKey,
  tierModifier,
  tilesFor,
  visibleSuggestions,
} from '../model/presentation';
import type { SuggestionFilter } from '../model/presentation';

/** A status chip: a ring-or-filled dot, then the label. The reference's shape, in two sizes. */
function Chip({
  tone,
  filled,
  label,
  title,
  small,
}: {
  readonly tone: string;
  readonly filled: boolean;
  readonly label: string;
  readonly title: string;
  readonly small?: boolean;
}): ReactElement {
  return (
    <span
      className={`lq-sg__chip lq-sg__chip--${tone}${small === true ? ' lq-sg__chip--sm' : ''}`}
      title={title}
    >
      <span className={`lq-sg__dot${filled ? ' lq-sg__dot--filled' : ''}`} />
      {label}
    </span>
  );
}

/** The five tiles: four figures and the AI status. */
function Summary({
  outcome,
  visible,
}: {
  readonly outcome: GenerationOutcome;
  readonly visible: readonly Suggestion[];
}): ReactElement {
  const { t } = useTranslation();
  const deterministic = visible.filter((item) => item.source !== 'model_proposed').length;
  const ai = aiPresentation(presentedStatus(outcome.model_status, deterministic));

  return (
    <div className="lq-sg__tiles">
      {tilesFor(outcome, visible).map((tile) => (
        <div className="lq-sg__tile" key={tile.label}>
          <div
            className={`lq-sg__tile-value${
              tile.modifier === '' ? '' : ` lq-sg__tile-value--${tile.modifier}`
            }`}
          >
            {tile.value}
          </div>
          <div className="lq-sg__eyebrow lq-sg__tile-label">{t(tile.label)}</div>
          <div className="lq-sg__note lq-sg__tile-hint">
            {t(tile.hint, tile.hintValues ?? {})}
          </div>
        </div>
      ))}

      {/* The AI status as a tile rather than a banner, so it sits *beside* the figures it
          qualifies. Its 3px top rule is the only place on this screen AI gets a visual of its own,
          and a rule rather than a tint keeps it from outweighing the four counts. */}
      <div
        className={`lq-sg__ai-tile${ai.tone === 'ok' ? ' lq-sg__ai-tile--ok' : ''}${
          ai.tone === 'warn' ? ' lq-sg__ai-tile--warn' : ''
        }`}
      >
        <div className="lq-sg__eyebrow lq-sg__tile-label" style={{ marginTop: 0 }}>
          {t('suggestions.ai.title')}
        </div>
        <Chip
          filled={ai.filled}
          label={t(ai.key)}
          title={t(`${ai.key}.note`)}
          tone={ai.tone}
        />
        <div className="lq-sg__ai-note">{t(`${ai.key}.note`)}</div>
      </div>
    </div>
  );
}

/** One evidence tier: the reference's card header, then the six-column table. */
function Tier({
  source,
  items,
  datasetId,
  canApply,
  editable,
  onCreate,
  openSuggestionId,
}: {
  readonly source: CandidateSource;
  readonly items: readonly Suggestion[];
  readonly datasetId: string;
  readonly canApply: boolean;
  readonly editable: Readonly<Record<string, readonly string[]>> | undefined;
  readonly onCreate: (suggestion: Suggestion) => void;
  /** The recommendation a link asked to open, on its apply panel. */
  readonly openSuggestionId: string | null;
}): ReactElement {
  const { t } = useTranslation();
  const stem = tierKey(source);
  const modifier = tierModifier(source);

  return (
    <div className="lq-sg__panel">
      <div className={`lq-sg__strip lq-sg__tier lq-sg__tier--${modifier}`}>
        <span className="lq-sg__eyebrow lq-sg__tier-title">{t(stem)}</span>
        <Chip
          filled={tierDotIsFilled(source)}
          label={t(`${stem}.badge`)}
          small
          title={t(`${stem}.meaning`)}
          tone={
            modifier === 'declared' || modifier === 'measured'
              ? 'ok'
              : modifier === 'inferred'
                ? 'info'
                : 'ai'
          }
        />
        <span className="lq-sg__tier-sub">{t(`${stem}.subtitle`)}</span>
        <span className="lq-sg__tier-count">
          {t('suggestions.group.count', { count: items.length })}
        </span>
      </div>

      <div className="lq-sg__scroll">
        <table className="lq-sg__table">
          <thead>
            <tr>
              <th scope="col">{t('suggestions.column.recommendation')}</th>
              <th scope="col">{t('suggestions.column.rationale')}</th>
              <th className="lq-sg__num" scope="col">
                {t('suggestions.column.confidence')}
              </th>
              <th className="lq-sg__num" scope="col">
                {t('suggestions.column.status')}
              </th>
            </tr>
          </thead>
          <tbody>
            {items.map((item) => (
              /* Keyed on the server's own id, which migration 0093 added for exactly this. */
              <RecommendationRow
                canApply={canApply}
                datasetId={datasetId}
                editable={editableParameters(editable, item.kind)}
                item={item}
                key={item.id}
                onOpenForm={onCreate}
                openOnArrival={item.id === openSuggestionId}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/** The collapsible boundaries panel. Collapsed by default, as the reference has it. */
function Boundaries(): ReactElement | null {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [checksOpen, setChecksOpen] = useState(false);
  const capabilities = useRulegenCapabilities();

  if (!capabilities.isSuccess) return null;
  const { proposable_kinds, never_proposed, checks, authorization_policy } =
    capabilities.data;

  return (
    <div className="lq-sg__panel" style={{ marginBottom: 0 }}>
      <div className="lq-sg__strip lq-sg__strip--plain">
        <div className="lq-sg__bounds-main">
          <div className="lq-sg__eyebrow lq-sg__bounds-title">
            {t('suggestions.boundaries.title')}
          </div>
          <div className="lq-sg__note">{t('suggestions.boundaries.hint')}</div>
        </div>
        <a
          className="lq-sg__chosen-swap"
          href="#"
          onClick={(event) => {
            event.preventDefault();
            setOpen((current) => !current);
          }}
        >
          {t(open ? 'suggestions.boundaries.collapse' : 'suggestions.boundaries.expand')}
        </a>
      </div>

      {!open && (
        <div className="lq-sg__bounds-summary">{t('suggestions.boundaries.summary')}</div>
      )}

      {open && (
        <div className="lq-sg__bounds-open">
          <div className="lq-sg__bounds-lead">{t('suggestions.boundaries.lead')}</div>
          {/* The server's own sentence, rendered rather than paraphrased. The bound is the
              *tenant*, and a screen restating a security control more strongly than the code
              enforces it is this repository's most-repeated defect — carrying the field means the
              claim cannot drift from the mechanism. */}
          <div className="lq-sg__bounds-body">{authorization_policy}</div>

          <div className="lq-sg__eyebrow lq-sg__bounds-heading">
            {t('suggestions.boundaries.proposes')}
          </div>
          <div className="lq-sg__kinds">
            {proposable_kinds.map((kind) => (
              <span className="lq-sg__kind" key={kind}>
                {kind}
              </span>
            ))}
          </div>

          <div className="lq-sg__eyebrow lq-sg__bounds-heading">
            {t('suggestions.boundaries.never')}
          </div>
          {/* Neutral rows with a strong left rule, **not** amber alerts. The previous build made
              these three warning banners, which reads as "something is wrong here"; a refusal by
              policy is a stated boundary rather than a fault. */}
          {Object.entries(never_proposed).map(([kind, reason]) => (
            <div className="lq-sg__never" key={kind}>
              <span className="lq-sg__never-kind">{kind}</span>
              <span className="lq-sg__never-why">{reason}</span>
            </div>
          ))}

          <div className="lq-sg__checks-toggle">
            <a
              className="lq-sg__chosen-swap"
              href="#"
              onClick={(event) => {
                event.preventDefault();
                setChecksOpen((current) => !current);
              }}
            >
              {t('suggestions.boundaries.showChecks', {
                shown: checksOpen ? 'yes' : 'no',
                count: checks.length,
              })}
            </a>
            {checksOpen && (
              <div className="lq-sg__checklist">
                {checks.map((check) => (
                  <div className="lq-sg__check" key={check}>
                    <span className="lq-sg__check-mark" />
                    <span className="lq-sg__check-text">{check}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export function SuggestionsScreen(): ReactElement {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const names = useDatasetNames();
  /* The design's three steps — Connection, Dataset, Rules — held as two choices. The step is derived
     from them rather than stored beside them, so the rail and the panel below it cannot disagree
     about where the reader is. */
  /* A link may name the source, the table and one recommendation — Auto Suggest on a dataset's Rules
     tab sends a recommendation that needs a new rule here, opened on its apply panel. Read once, as
     the starting choice: the reader moves on from it with the same steps as ever. Both ids are
     needed, because the steps are connection then dataset and a dataset alone has no step 2. */
  const [searchParams] = useSearchParams();
  const linked =
    (searchParams.get('connection') ?? '') !== '' &&
    (searchParams.get('dataset') ?? '') !== '';
  const [connectionId, setConnectionId] = useState(
    linked ? (searchParams.get('connection') ?? '') : '',
  );
  const [datasetId, setDatasetId] = useState(
    linked ? (searchParams.get('dataset') ?? '') : '',
  );
  const [openSuggestionId] = useState(linked ? searchParams.get('suggestion') : null);
  const step = connectionId === '' ? 1 : datasetId === '' ? 2 : 3;
  const { groups: sourceGroups } = useSuggestionCatalogue();
  const nameOfGroup = useGroupName();
  const chosenGroup = sourceGroups.find((group) => group.connectionId === connectionId);
  const suggestions = useSuggestions(datasetId === '' ? undefined : datasetId);
  const regenerate = useRegenerateSuggestions();

  /* The outcome from the regeneration *this session* performed, and nothing else. The GET returns
     only the suggestion list — by design, because a run's counts describe a run rather than a
     dataset's current state, and serving them from the list would attribute one generation's
     numbers to whatever the list holds now. */
  const session = regenerate.data?.dataset_id === datasetId ? regenerate.data : undefined;
  const listing = suggestions.data;
  /* **The latest stored run stands in when this session has none**, which is the ordinary case now
     that a profile regenerates recommendations on its own. It names its trigger and time in the run
     note, so its figures are never mistaken for a run the reader caused. */
  const lastRun = listing?.last_run ?? null;
  const outcome =
    session ?? (lastRun === null ? undefined : outcomeFromRun(datasetId, lastRun));
  const canApply = listing?.can_apply ?? false;
  const capabilities = useRulegenCapabilities();
  const [filter, setFilter] = useState<SuggestionFilter>(NO_FILTER);
  const busy = regenerate.isPending && regenerate.variables === datasetId;
  const failed = regenerate.isError && regenerate.variables === datasetId;
  const hasRun = outcome !== undefined;

  const datasetName = names.data?.byId.get(datasetId);

  /* Choosing a different dataset drops the previous dataset's failure. Without this a 409 saying
     "no columns of *this dataset* are visible to you" stays on screen beside the newly chosen
     table — the one message the backend deliberately frames as a permissions answer,
     misattributed to a dataset that is fine. */
  const reset = regenerate.reset;
  useEffect(() => {
    reset();
    setFilter(NO_FILTER);
  }, [datasetId, reset]);

  const stored = suggestions.data?.suggestions ?? [];
  const status = outcome?.model_status ?? 'not_configured';
  /* **Status first, tier second.** A model row is shown only on a run whose status is
     `contributed` — see `visibleSuggestions`, which is the reference's own invariant. */
  const visible = visibleSuggestions(stored, status);
  const filtered = visible.filter((item) => matchesFilter(item, filter));
  const groups = groupedByTier(filtered);
  const observations = session?.observations ?? lastRun?.observations ?? [];
  const deterministic = visible.filter((item) => item.source !== 'model_proposed').length;
  const presented = presentedStatus(status, deterministic);
  const ai = aiPresentation(presented);
  const nothingFound = hasRun && !busy && visible.length === 0;

  return (
    <div className="lq-sg">
      <div className="lq-sg__head">
        <h1 className="lq-sg__title">{t('suggestions.title')}</h1>
        <p className="lq-sg__lede">{t('suggestions.lede')}</p>
      </div>
      <StepTrail
        label={t('suggestions.steps.label')}
        current={step}
        onStep={(n) => {
          if (n === 1) {
            setConnectionId('');
            setDatasetId('');
          }
          if (n === 2) setDatasetId('');
        }}
        steps={[
          {
            title: t('suggestions.steps.connection'),
            sub:
              chosenGroup === undefined
                ? t('suggestions.steps.chooseSource')
                : nameOfGroup(chosenGroup),
          },
          {
            title: t('suggestions.steps.dataset'),
            sub:
              datasetId !== ''
                ? (datasetName ?? datasetId)
                : step > 1
                  ? t('suggestions.steps.chooseTable')
                  : '—',
          },
          {
            title: t('suggestions.steps.rules'),
            sub: step === 3 ? t('suggestions.steps.review') : '—',
          },
        ]}
      />

      {step === 1 && (
        <SuggestionSourcePicker
          onPick={(id) => {
            setConnectionId(id);
            setDatasetId('');
          }}
        />
      )}

      {step === 2 && (
        <SuggestionTablePicker
          connectionId={connectionId}
          onPick={(id) => {
            setDatasetId(id);
          }}
        />
      )}

      {/* ------------------------------------------------------------- the chosen dataset */}
      {step === 3 && (
        <div className="lq-sg__panel lq-sg__chosenCard">
          <div className="lq-sg__chosenMain">
            <span className="lq-sg__chosen-name">
              <DatasetObjectTypeIcon objectType={names.data?.kindById.get(datasetId)} />
              {datasetName ?? datasetId}
            </span>
            {/* The column count is a fact about a run, so it is shown once this session has one —
                the listing stores no run, and a remembered count would describe a schema that may
                have moved. */}
            {hasRun && (
              <span className="lq-sg__note">
                {t('suggestions.pick.meta', { count: outcome.columns_considered })}
              </span>
            )}
          </div>
          <button
            className="lq-sg__go"
            disabled={busy}
            onClick={() => {
              regenerate.mutate(datasetId);
            }}
            title={t('suggestions.pick.replaces')}
            type="button"
          >
            {busy && <span className="lq-sg__spinner" />}
            <span>
              {t(
                busy
                  ? 'suggestions.generating'
                  : hasRun || stored.length > 0
                    ? 'suggestions.generateAgain'
                    : 'suggestions.generate',
              )}
            </span>
          </button>
        </div>
      )}

      {/* ------------------------------------------------------------- generation failed */}
      {failed && (
        <>
          <div className="lq-sg__fail">
            <div className="lq-sg__fail-main">
              <div className="lq-sg__fail-title">
                {t('suggestions.fail.title', { dataset: datasetName ?? datasetId })}
              </div>
              <div className="lq-sg__fail-text">{regenerate.error.message}</div>
            </div>
            <button
              className="lq-sg__act"
              onClick={() => {
                regenerate.mutate(datasetId);
              }}
              type="button"
            >
              {t('suggestions.fail.retry')}
            </button>
          </div>
          {/* A failed refresh must not erase a result that worked — the reference says so, and this
              is where it is true: the rows below come from the listing, which a failed mutation
              does not invalidate. */}
          {visible.length > 0 && (
            <div className="lq-sg__note lq-sg__kept">{t('suggestions.fail.kept')}</div>
          )}
        </>
      )}

      {/* The stored list could not be read. Said as a failure of the read — "nothing generated" here
          would be a claim about this table that a request which never answered has no grounds for. */}
      {step === 3 && !busy && suggestions.isError && (
        <div className="lq-sg__fail">
          <div className="lq-sg__fail-main">
            <div className="lq-sg__fail-title">
              {t('suggestions.readFailed', { dataset: datasetName ?? datasetId })}
            </div>
            <div className="lq-sg__fail-text">{suggestions.error.message}</div>
          </div>
          <button
            className="lq-sg__act"
            onClick={() => {
              void suggestions.refetch();
            }}
            type="button"
          >
            {t('suggestions.fail.retry')}
          </button>
        </div>
      )}

      {/* ------------------------------------------------------------- loading */}
      {busy && (
        <>
          <div className="lq-sg__tiles">
            {[0, 1, 2, 3, 4].map((slot) => (
              <div className="lq-sg__tile" key={slot}>
                <div className="lq-sg__skel lq-sg__skel-tile-value" />
                <div className="lq-sg__skel lq-sg__skel-tile-label" />
              </div>
            ))}
          </div>
          <div className="lq-sg__panel">
            {[0, 1, 2, 3, 4, 5].map((slot) => (
              <div className="lq-sg__skel-row" key={slot}>
                <div className="lq-sg__skel lq-sg__skel-bar lq-sg__skel-bar--kind" />
                <div className="lq-sg__skel lq-sg__skel-bar lq-sg__skel-bar--cols" />
                <div className="lq-sg__skel lq-sg__skel-bar lq-sg__skel-bar--why" />
              </div>
            ))}
          </div>
        </>
      )}

      {/* ------------------------------------------------------------- the run's figures */}
      {!busy && hasRun && (
        <RunNote
          profileStatus={session?.profile_status}
          run={session === undefined ? lastRun : null}
        />
      )}
      {!busy && hasRun && <Summary outcome={outcome} visible={visible} />}
      {!busy && hasRun && (
        /* The gaps come only with a fresh generation; a stored run keeps none, so after a reload
           an empty dimension reads "not known" rather than "covered". */
        <CoverageReadout
          readout={coverageByDimension(
            itemsFromSuggestions(visible),
            session?.coverage_gaps ?? null,
          )}
          selected={filter.category}
          onSelect={(dimension) => {
            /* Every status, because a dimension's rows may be applied or dismissed rather than
               open — narrowing to "To review" there would show an empty list beside a full cell. */
            setFilter((current) =>
              current.category === dimension
                ? { ...current, category: '' }
                : { ...current, category: dimension, status: 'all' },
            );
          }}
        />
      )}
      {!busy && <Observations observations={observations} />}

      {/* ------------------------------------------------------------- filters */}
      {!busy && stored.length > 0 && (
        <FilterBar
          counts={statusCounts(visible)}
          filter={filter}
          onChange={setFilter}
          originCounts={originCounts(visible, filter)}
        />
      )}
      {!busy && stored.length > 0 && filtered.length === 0 && (
        <div className="lq-sg__note lq-sg__kept lq-sg__nomatch">
          <span>{t('suggestions.filter.none')}</span>
          {isFiltered(filter) && (
            <button
              type="button"
              className="lq-sg__act lq-sg__act--quiet"
              onClick={() => {
                setFilter(NO_FILTER);
              }}
            >
              {t('suggestions.filter.clear')}
            </button>
          )}
        </div>
      )}

      {/* ------------------------------------------------------------- the tiers */}
      {!busy &&
        groups.map((group) => (
          <Tier
            canApply={canApply}
            datasetId={datasetId}
            editable={capabilities.data?.editable_parameters}
            items={group.items}
            key={group.source}
            openSuggestionId={openSuggestionId}
            onCreate={(item) => {
              navigate(SCREEN_PATHS.rules, {
                state: { suggestionId: item.id } satisfies RulePrefill,
              });
            }}
            source={group.source}
          />
        ))}

      {/* Where the AI tier would be: the status chip and its sentence. Never an empty heading, and
          never AI wording on a result no model touched. */}
      {!busy && hasRun && showsAiStandIn(presented, visible.length) && (
        <div
          className={`lq-sg__ai-absent${
            ai.tone === 'warn' ? ' lq-sg__ai-absent--warn' : ''
          }`}
        >
          <div className="lq-sg__ai-absent-row">
            <Chip
              filled={ai.filled}
              label={t(ai.key)}
              title={t(`${ai.key}.note`)}
              tone={ai.tone}
            />
            <span className="lq-sg__ai-absent-note">{t(`${ai.key}.note`)}</span>
          </div>
          {/* The server's own sentence for *why*, which the generic note cannot carry: an
              exhausted provider quota read as "could not be reached" while the provider was
              answering every call with a 429 naming the cause. Host-written, never model text
              (`model_unavailable_reason` in ai-rulegen.yaml), so it is safe to render as is. */}
          {(presented === 'unavailable' || presented === 'rejected') &&
            outcome.model_unavailable_reason && (
              <p className="lq-sg__ai-absent-reason">
                {outcome.model_unavailable_reason}
              </p>
            )}
        </div>
      )}

      {/* ------------------------------------------------------------- the four empty states */}
      {datasetId !== '' &&
        !busy &&
        !hasRun &&
        !failed &&
        suggestions.isSuccess &&
        stored.length === 0 && (
          <div className="lq-sg__empty">
            <div className="lq-sg__empty-title">
              {t('suggestions.empty.never.title', { dataset: datasetName ?? datasetId })}
            </div>
            <div className="lq-sg__empty-body lq-sg__empty-body--narrow">
              {t('suggestions.empty.never.body')}
            </div>
          </div>
        )}

      {/* Thin and none are different answers and the reference separates them. A
          `columns_considered` of zero is the only honest reading of "not enough metadata" this
          platform can give: the catalogue holds the dataset and nothing about its columns.
          Above zero and still empty is the other answer — every column was looked at and none
          justified a rule. */}
      {nothingFound && (
        <div
          className={
            outcome.columns_considered === 0
              ? 'lq-sg__empty lq-sg__empty--thin'
              : 'lq-sg__empty lq-sg__empty--answer'
          }
        >
          <div className="lq-sg__empty-title">
            {t(
              outcome.columns_considered === 0
                ? 'suggestions.empty.thin.title'
                : 'suggestions.empty.none.title',
            )}
          </div>
          <div className="lq-sg__empty-body lq-sg__empty-body--wide">
            {t(
              outcome.columns_considered === 0
                ? 'suggestions.empty.thin.body'
                : 'suggestions.empty.none.body',
              { count: outcome.columns_considered },
            )}
          </div>
        </div>
      )}

      <Boundaries />
    </div>
  );
}
